import {
    Autocomplete,
    Button,
    Calendar,
    Checkbox,
    Fieldset,
    Grid,
    GridCol,
    Infobox,
    Input,
    ListBoxOptionType,
    ListBoxOptionsType,
    Select,
    SelectGroup,
    SelectOption,
    Skeleton,
    Spinner,
    Text,
} from '@flixbus/honeycomb-react';
import { Icon, IconBus } from '@flixbus/honeycomb-icons-react';
import React, { useEffect, useMemo, useState } from 'react';
import { legacyDateToTemporal, printDate, temporalToLegacyDate } from '@ridehub/util-temporal';
import { useFeatureFlags, useLineRidesSearch, useLines } from '@ridehub/data-ride';

import { Temporal } from '@js-temporal/polyfill';
import { buildTrie } from './autocomplete-trie';
import styles from './feat-line-rides-search.module.scss';
import { useRidesSearchContext } from '../rides-search-ctx';
import { useSLIContext } from '@ridehub/util-datadog';
import { useSearchParams } from 'react-router-dom';

interface FormFieldState<T> {
    value: T;
    dirty: boolean;
    error?: string;
}

enum TripNumberSelectState {
    Odd = 'odd',
    Even = 'even',
    EvenAndOdd = 'even_and_odd',
    Exact = 'exact',
}

interface TripNumberFilterDefaults {
    tripNumberFilter: TripNumberSelectState;
    exactTripNumber: number | undefined;
}

function deriveDefaultTripNumberFilter(
    tripNumberFilter: string | null,
    exactTripNumber: string | null
): TripNumberFilterDefaults {
    const validStates: Array<string> = Object.values(TripNumberSelectState);
    if (
        !tripNumberFilter ||
        !validStates.includes(tripNumberFilter) ||
        (tripNumberFilter === TripNumberSelectState.Exact && exactTripNumber === null)
    ) {
        return {
            tripNumberFilter: TripNumberSelectState.EvenAndOdd,
            exactTripNumber: undefined,
        };
    }
    return {
        tripNumberFilter: tripNumberFilter as TripNumberSelectState,
        exactTripNumber: exactTripNumber === null ? undefined : parseInt(exactTripNumber),
    };
}

function deriveDefaultWeekdays(wd: string | null) {
    const placeholder = new Array(7).fill(undefined);
    if (!wd) {
        return placeholder.map((slot) => true);
    }
    const selected = wd.split('-').map((el) => parseInt(el));
    return placeholder.map((slot, idx) => selected.includes(idx + 1));
}

function encodeWeekdaysToQueryString(wd: Array<boolean>): string {
    return wd
        .map((isSelected, idx) => (isSelected ? `${idx + 1}` : false))
        .filter((isSelected) => !!isSelected)
        .join('-');
}

export function FeatLineRidesSearch(props: { auth?: boolean }) {
    const authReqests = props.auth === undefined ? true : props.auth;
    const { reportSliGoodEvent } = useSLIContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const [submitted, setSubmitted] = useState(searchParams.get('submitted') === 'true');
    const lineQueryState = useLines(authReqests);
    const { data: featureFlags } = useFeatureFlags(authReqests);

    const [lineCodesField, setLineCodesField] = React.useState<FormFieldState<Array<ListBoxOptionType>>>({
        value: [],
        dirty: false,
    });

    const cal = new Temporal.Calendar('iso8601');
    const today = Temporal.Now.plainDate(cal);
    const twoYears = new Temporal.Duration(2);
    const oneMonth = new Temporal.Duration(undefined, 1);

    const calStartDate = today.subtract(twoYears);
    const calEndDate = today.add(twoYears);
    const departureStartQP = searchParams.get('departure_start');
    const defaultDepartureStart = departureStartQP ? Temporal.PlainDate.from(departureStartQP) : today;
    const departureEndQP = searchParams.get('departure_end');
    const defaultDepartureEnd = departureEndQP
        ? Temporal.PlainDate.from(departureEndQP)
        : defaultDepartureStart.add(oneMonth);
    const [showCalendar, setShowCalendar] = useState(false);
    const [departureStart, setDepartureStart] = React.useState(defaultDepartureStart);
    const [departureEnd, setDepartureEnd] = React.useState<Temporal.PlainDate | undefined>(defaultDepartureEnd);

    const defaultTripNumberFilter = deriveDefaultTripNumberFilter(
        searchParams.get('trip_number_filter'),
        searchParams.get('trip_number')
    );
    const [tripNumberFilter, setTripNumberFilter] = React.useState<TripNumberSelectState>(
        defaultTripNumberFilter.tripNumberFilter
    );
    const [exactTripNumber, setExactTripNumber] = React.useState<number | undefined>(
        defaultTripNumberFilter.exactTripNumber
    );

    const [selectedWeekdays, setSelectedWeekdays] = React.useState<Array<boolean>>(
        deriveDefaultWeekdays(searchParams.get('weekdays'))
    );

    const [bidPriceActiveOnly, setBidPriceActiveOnly] = React.useState<boolean>(
        searchParams.get('bid_price_active_only') === 'true'
    );

    const { isPending, data, error } = useLineRidesSearch(
        lineCodesField.value.map((lc: ListBoxOptionType) => `${lc.value}`),
        departureStart,
        tripNumberFilter,
        encodeWeekdaysToQueryString(selectedWeekdays),
        departureEnd,
        exactTripNumber,
        bidPriceActiveOnly,
        submitted,
        authReqests
    );
    const ridesSearchCtx = useRidesSearchContext();

    const linesTrie = useMemo(() => {
        if (lineQueryState.data) {
            const words = lineQueryState.data.map((el) => {
                const dashNoSpaceRegex = /\S-\S/g;
                return {
                    word: `${el.code}: ${el.title
                        .replace('<>', '-')
                        .replace(dashNoSpaceRegex, (match) => `${match.replace('-', ' - ')}`)}`,
                    value: el.code,
                };
            });
            return buildTrie(words);
        } else {
            return buildTrie([]);
        }
    }, [lineQueryState.data]);

    function deriveLinesFromQueryParams() {
        const params = searchParams.get('line_codes');
        if (!params) {
            return [];
        }
        const lineCodes = params.split('_');
        return lineCodes
            .map((lc) => {
                const res = linesTrie.search(lc);
                return res.find((val) => val.value === lc);
            })
            .filter((line) => line !== undefined)
            .map((line) => {
                return { title: line.word, value: line.value };
            });
    }

    useEffect(() => {
        if (data) {
            ridesSearchCtx.setRidesSearchResults(data.rides);
            ridesSearchCtx.setEmptySearchResults(data.rides.length === 0);
        } else {
            ridesSearchCtx.setRidesSearchResults([]);
            ridesSearchCtx.setEmptySearchResults(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (lineQueryState.data) {
            setLineCodesField({ ...lineCodesField, value: deriveLinesFromQueryParams() });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lineQueryState.data]);

    function submitSearchOnEnter(e: KeyboardEvent) {
        if (e.key === 'Enter') {
            submitSearch();
        }
    }

    function submitSearch() {
        function validateForm(): boolean {
            if (lineCodesField.value.length === 0) {
                setLineCodesField({ ...lineCodesField, error: 'Missing Line Code' });
                return false;
            }
            if (tripNumberFilter === TripNumberSelectState.Exact && exactTripNumber === undefined) {
                // TODO: add error to UI
                return false;
            }
            return true;
        }
        if (validateForm()) {
            setSubmitted(true);
            const params = new URLSearchParams();
            params.set('line_codes', lineCodesField.value.map((lc) => `${lc.value}`).join('_'));
            params.set('submitted', 'true');
            params.set('departure_start', departureStart.toString());
            params.set('departure_end', departureEnd?.toString() as string);
            params.set('trip_number_filter', tripNumberFilter);
            if (tripNumberFilter === TripNumberSelectState.Exact) {
                params.set('trip_number', `${exactTripNumber}`);
            }
            let weekdays = encodeWeekdaysToQueryString(selectedWeekdays);
            if (weekdays.length === 0) {
                weekdays = encodeWeekdaysToQueryString(deriveDefaultWeekdays(null));
            }
            params.set('weekdays', weekdays);
            if (bidPriceActiveOnly) {
                params.set('bid_price_active_only', 'true');
            }
            setSearchParams(params);
        }
    }

    function displayDateRange() {
        if (departureStart && departureEnd) {
            return `${printDate(departureStart, false)} - ${printDate(departureEnd, false)}`;
        }
        if (departureStart) {
            return `${printDate(departureStart, false)} -`;
        }
        return '-';
    }

    async function asyncFilterOptions(inputValue: string, options: ListBoxOptionsType<ListBoxOptionType>) {
        const empty: Array<ListBoxOptionType> = [];
        if (inputValue === '') {
            return Promise.resolve(empty);
        }
        const suggestions = linesTrie.search(inputValue);
        if (suggestions.length === 0) {
            return Promise.resolve(empty);
        }
        return Promise.resolve(
            suggestions.map((searchResult) => {
                return {
                    title: searchResult.word,
                    value: searchResult.value,
                };
            })
        );
    }

    if (!lineQueryState.data) {
        return (
            <Grid extraClasses={styles.searchContainer}>
                <GridCol size={5}>
                    <Skeleton height="xs" width="sm" extraClasses={styles.inputSkeleton} />
                    <Skeleton height="lg" width="lg" extraClasses={styles.inputSkeleton} />
                    <Grid>
                        <GridCol size={6}>
                            <Skeleton height="xs" width="sm" extraClasses={styles.inputSkeleton} />
                            <Skeleton height="lg" width="lg" extraClasses={styles.inputSkeleton} />
                        </GridCol>
                    </Grid>
                </GridCol>
                <GridCol size={3}>
                    <Skeleton height="xs" width="md" extraClasses={styles.inputSkeleton} />
                    <Skeleton height="lg" width="lg" extraClasses={styles.inputSkeleton} />
                </GridCol>
                <GridCol size={2}>
                    <Skeleton height="xs" width="sm" extraClasses={`${styles.inputSkeleton} ${styles.invisible}`} />
                    <Skeleton height="lg" width="sm" extraClasses={styles.inputSkeleton} />
                </GridCol>
            </Grid>
        );
    }

    if (!submitted) {
        // In case the user did not submit e search we can consider the page-load
        // to be successful once the filter inputs are shown.
        // Submitted searches will report 'sucess' once the results table is rendered.
        reportSliGoodEvent();
    }

    return (
        <Grid extraClasses={styles.searchContainer}>
            <GridCol size={5}>
                <Grid>
                    <GridCol size={12}>
                        <Autocomplete
                            extraClasses={styles.autocomplete}
                            multiple
                            onDebounce={asyncFilterOptions}
                            onChange={(selectedOptions: ListBoxOptionType[]) => {
                                setLineCodesField({ value: selectedOptions, dirty: true, error: undefined });
                                if (submitted) {
                                    setSubmitted(false);
                                }
                            }}
                            defaultValue={deriveLinesFromQueryParams()}
                            comboboxProps={{
                                iconLeft: <Icon InlineIcon={IconBus} />,
                                limitSelectedOptions: 3,
                                label: 'Lines',
                                resetProps: {
                                    label: 'Clear',
                                },
                                tagProps: (selectedOption) => ({
                                    renderTag: () => `${selectedOption.value}`,
                                }),
                                valid: lineCodesField.error ? false : undefined,
                                infoError: 'Line Code is mandatory',
                            }}
                            listBoxProps={{
                                optionsToDisplay: 10,
                            }}
                            onKeyDown={submitSearchOnEnter as unknown as React.KeyboardEventHandler<HTMLInputElement>}
                        />
                    </GridCol>
                </Grid>
                <Grid extraClasses={styles.secondInputRow}>
                    <GridCol size={6}>
                        <Select
                            id="trip-number-choice"
                            label="Trip Number"
                            onChange={(e) => {
                                if (submitted) {
                                    setSubmitted(false);
                                }
                                setTripNumberFilter(e.target.value as TripNumberSelectState);
                            }}
                            value={tripNumberFilter}
                        >
                            <SelectOption value={TripNumberSelectState.EvenAndOdd}>Even and Odd</SelectOption>
                            <SelectOption value={TripNumberSelectState.Even}>Even</SelectOption>
                            <SelectOption value={TripNumberSelectState.Odd}>Odd</SelectOption>
                            <SelectOption value={TripNumberSelectState.Exact}>Exact</SelectOption>
                        </Select>
                    </GridCol>
                    <GridCol size={6}>
                        {tripNumberFilter === TripNumberSelectState.Exact && (
                            <Input
                                name="trip-number-exact"
                                id="trip-number-exact"
                                label="is"
                                type="number"
                                inlineLabelLeft="TN"
                                value={exactTripNumber ? exactTripNumber : ''}
                                onChange={(e) => {
                                    if (submitted) {
                                        setSubmitted(false);
                                    }
                                    setExactTripNumber(e.target.value ? parseInt(e.target.value) : undefined);
                                }}
                            />
                        )}
                    </GridCol>
                </Grid>
            </GridCol>
            <GridCol size={5}>
                <Grid>
                    <GridCol size={7}>
                        <Fieldset itemSpacing="half">
                            <Input
                                readOnly
                                label="Departure dates"
                                id="departure-dates"
                                value={displayDateRange()}
                                aria-live="polite"
                                onFocus={() => setShowCalendar(true)}
                                extraClasses={styles.heightFix}
                            />
                            {showCalendar && (
                                <Calendar
                                    id="roundtrip-date-picker"
                                    aria-controls="departure-date return-date"
                                    days={weekDaysEN}
                                    months={monthsEN}
                                    horizontal
                                    startDate={temporalToLegacyDate(calStartDate)}
                                    endDate={temporalToLegacyDate(calEndDate)}
                                    startSelected={temporalToLegacyDate(departureStart)}
                                    endSelected={departureEnd ? temporalToLegacyDate(departureEnd) : undefined}
                                    appearance="compact"
                                    handleSelect={(date) => {
                                        if (submitted) {
                                            setSubmitted(false);
                                        }
                                        const temp = legacyDateToTemporal(date);
                                        if (departureStart === undefined) {
                                            setDepartureStart(temp);
                                            return;
                                        }
                                        if (departureEnd === undefined) {
                                            setDepartureEnd(temp);
                                            setShowCalendar(false);
                                            return;
                                        }
                                        if (departureEnd !== undefined && departureStart !== undefined) {
                                            setDepartureEnd(undefined);
                                            setDepartureStart(temp);
                                            return;
                                        }
                                    }}
                                    extraClasses={styles.calendar}
                                />
                            )}
                        </Fieldset>
                    </GridCol>
                </Grid>
                <Grid extraClasses={styles.secondInputRow}>
                    <GridCol size={12}>
                        <SelectGroup
                            multi={true}
                            label="Weekdays"
                            options={weekDaysEN.map((weekday, idx) => {
                                const weekdayOption = {
                                    id: idx + 1,
                                    value: idx + 1,
                                    name: weekday.long,
                                    label: weekday.short,
                                    onClick: (e: React.ChangeEvent<HTMLInputElement>) => {
                                        if (submitted) {
                                            setSubmitted(false);
                                        }
                                        const update = [...selectedWeekdays];
                                        update[parseInt(e.target.value) - 1] = e.target.checked;
                                        setSelectedWeekdays(update);
                                    },
                                    defaultChecked: selectedWeekdays[idx],
                                };
                                return weekdayOption;
                            })}
                            id="weekday-select"
                        />
                    </GridCol>
                </Grid>
                {featureFlags?.bid_price_filter && (
                    <Grid extraClasses={styles.secondInputRow}>
                        <GridCol size={12}>
                            <Checkbox
                                id="bid-price-active-filter"
                                label="Bid Price Active only"
                                value="bid_price_active_only"
                                checked={bidPriceActiveOnly}
                                onChange={(e) => {
                                    if (submitted) {
                                        setSubmitted(false);
                                    }
                                    setBidPriceActiveOnly(e.target.checked);
                                }}
                            />
                        </GridCol>
                    </Grid>
                )}
            </GridCol>
            <GridCol size={2}>
                <Button
                    appearance="primary"
                    extraClasses={styles.submitButton}
                    onClick={submitSearch}
                    data-dd-action-name="View Search Results"
                >
                    {isPending && submitted ? <Spinner size="sm" /> : 'Search'}
                </Button>
            </GridCol>
            <GridCol>
                {error && (
                    <Infobox aria-label="Some critical error occurred" appearance="danger">
                        <Text>This search caused a technical error. {error.message}</Text>
                    </Infobox>
                )}
            </GridCol>
        </Grid>
    );
}

const weekDaysEN = [
    { short: 'Mo', long: 'Monday' },
    { short: 'Tu', long: 'Tuesday' },
    { short: 'We', long: 'Wednesday' },
    { short: 'Th', long: 'Thursday' },
    { short: 'Fr', long: 'Friday' },
    { short: 'Sa', long: 'Saturday' },
    { short: 'Su', long: 'Sunday' },
];
const monthsEN = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
